import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../Firebase';
import { useLogin } from '../hooks/useLogin';
import './EmailLinkSignIn.css';

// Finishes "Email me a sign-in link" (Login page, and sign-up when the email
// already has an account): when someone opens the link from their email, it
// lands on the site with Firebase's sign-in code in the URL — this signs them
// in and takes them on to where the link points. If they open it in a
// different browser from the one they asked in (common from the Facebook /
// Instagram app), we don't know their email, so we ask for it.
const EMAIL_KEY = 'emailForSignIn';
// Read once at start-up: pages that need a login (e.g. /ProfileUpdate)
// redirect to /login straight away, which would drop the code from the URL.
const INITIAL_HREF = typeof window !== 'undefined' ? window.location.href : '';

const EmailLinkSignIn = () => {
  const navigate = useNavigate();
  const { login } = useLogin();
  const [needEmail, setNeedEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const finish = async (emailAddress) => {
    setBusy(true);
    setError('');
    try {
      const href = INITIAL_HREF;
      const result = await auth.signInWithEmailLink(emailAddress, href);
      try { window.localStorage.removeItem(EMAIL_KEY); } catch { /* ignore */ }
      await login(result.user.uid);
      // Drop Firebase's one-time code from the address, keep the page.
      const url = new URL(href);
      ['apiKey', 'oobCode', 'mode', 'lang', 'continueUrl'].forEach((k) => url.searchParams.delete(k));
      setNeedEmail(false);
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
    } catch (err) {
      setNeedEmail(true);
      setError(err.code === 'auth/invalid-email'
        ? "That email doesn't match the link. Please check it."
        : 'This sign-in link has expired or was already used. Request a new one from the Login page.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let isLink = false;
    try { isLink = auth.isSignInWithEmailLink(INITIAL_HREF); } catch { /* ignore */ }
    if (!isLink) return;
    let saved = null;
    try { saved = window.localStorage.getItem(EMAIL_KEY); } catch { /* ignore */ }
    if (saved) finish(saved);
    else setNeedEmail(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!needEmail) return null;

  return (
    <div className="email-link-overlay">
      <form
        className="email-link-card"
        onSubmit={(e) => { e.preventDefault(); if (email.trim()) finish(email.trim()); }}
      >
        <h3>Confirm your email to log in</h3>
        <p>Enter the email address the sign-in link was sent to.</p>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
        {error && <p className="email-link-error">{error}</p>}
        <button className="button" type="submit" disabled={busy}>{busy ? 'Logging in…' : 'Log in'}</button>
      </form>
    </div>
  );
};

export default EmailLinkSignIn;
